import { ChartConfiguration, ChartData, TooltipItem, ChartTypeRegistry } from 'chart.js';
import { AircraftCategoryNameEnum } from '../models';

export interface BaseOffer {
  categoryName: string;
  offerPrice: number;
  offerPriceInCurrency?: number;
  currency?: string;
  modelName?: string;
}

export const CHART_CONSTANTS = {
  COLORS: {
    YOUR_OFFERS: 'rgb(198, 38, 44)',
    COMPETITOR_OFFERS: 'gray'
  },
  POINT: {
    YOUR_OFFERS: {
      RADIUS: 6,
      HOVER_RADIUS: 6,
      BORDER_WIDTH: 0
    },
    COMPETITOR_OFFERS: {
      RADIUS: 7,
      HOVER_RADIUS: 7,
      BORDER_WIDTH: 0
    }
  },
  DIMENSIONS: {
    BASE_HEIGHT: 135,
    HEIGHT_PER_CATEGORY: 75,
    IMAGE: {
      WIDTH: 85,
      HEIGHT: 60,
      OFFSET_X: 90
    }
  },
  SCALES: {
    X: {
      DEFAULT_MIN: 4000,
      DEFAULT_MAX: 6000,
      STEP_SIZE: 500,
      PADDING: 1000
    },
    Y: {
      MIN: 0,
      STEP_SIZE: 1
    }
  },
  LAYOUT: {
    PADDING_LEFT: 75
  }
} as const;

export interface ChartPoint<T extends BaseOffer = BaseOffer> {
  x: number;
  y: number;
  offer: T;
}

export interface CategoryMapping {
  [categoryName: string]: number;
}

export interface ChartCreationInput<T extends BaseOffer = BaseOffer> {
  competitorOffers: Array<T>;
  yourOffers: Array<T>;
}

export interface ScaleConfig {
  min: number;
  max: number;
  ticks: Array<number>;
}

export function createCategoryMapping<T extends BaseOffer>(offers: Array<T>): CategoryMapping {
  const uniqueCategories = [...new Set(offers.map(offer => offer.categoryName))];

  return uniqueCategories.reduce((acc, category, index) => {
    acc[category] = index + 1;
    return acc;
  }, {} as CategoryMapping);
}

export function transformOffersToPoints<T extends BaseOffer>(
  offers: Array<T>,
  categoryMapping: CategoryMapping
): Array<ChartPoint> {
  return offers.map(offer => ({
    x: offer.offerPrice,
    y: categoryMapping[offer.categoryName],
    offer
  }));
}

export function createChartDatasets(input: ChartCreationInput, categoryMapping: CategoryMapping): ChartData<'scatter'>['datasets'] {
  const { competitorOffers, yourOffers } = input;
  const datasets = [];

  if (yourOffers.length > 0) {
    datasets.push({
      label: 'Your Offers',
      backgroundColor: CHART_CONSTANTS.COLORS.YOUR_OFFERS,
      pointRadius: CHART_CONSTANTS.POINT.YOUR_OFFERS.RADIUS,
      pointHoverRadius: CHART_CONSTANTS.POINT.YOUR_OFFERS.HOVER_RADIUS,
      pointBorderWidth: CHART_CONSTANTS.POINT.YOUR_OFFERS.BORDER_WIDTH,
      data: transformOffersToPoints(yourOffers, categoryMapping)
    });
  }

  datasets.push({
    label: 'Competitor Offers',
    backgroundColor: CHART_CONSTANTS.COLORS.COMPETITOR_OFFERS,
    pointRadius: CHART_CONSTANTS.POINT.COMPETITOR_OFFERS.RADIUS,
    pointHoverRadius: CHART_CONSTANTS.POINT.COMPETITOR_OFFERS.HOVER_RADIUS,
    pointBorderWidth: CHART_CONSTANTS.POINT.COMPETITOR_OFFERS.BORDER_WIDTH,
    data: transformOffersToPoints(competitorOffers, categoryMapping)
  });

  return datasets;
}

export function createChartData(input: ChartCreationInput, categoryMapping: CategoryMapping): ChartData<'scatter'> {
  return {
    datasets: createChartDatasets(input, categoryMapping)
  };
}

export function calculateOptimalTicks(min: number, max: number, desiredCount = 7): Array<number> {
  if (min >= max) return [min];

  const rawStep = (max - min) / desiredCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  let niceFraction = rawStep / magnitude;

  if (niceFraction <= 1) niceFraction = 1;
  else if (niceFraction <= 2) niceFraction = 2;
  else if (niceFraction <= 5) niceFraction = 5;
  else niceFraction = 10;

  const stepSize = niceFraction * magnitude;
  const ticks: Array<number> = [];

  for (let i = min; i <= max; i += stepSize) {
    ticks.push(Math.round(i));
  }

  return ticks;
}

export function calculateXScaleConfig<T extends BaseOffer>(offers: Array<T>): ScaleConfig {
  if (offers.length === 0) {
    return {
      min: CHART_CONSTANTS.SCALES.X.DEFAULT_MIN,
      max: CHART_CONSTANTS.SCALES.X.DEFAULT_MAX,
      ticks: calculateOptimalTicks(CHART_CONSTANTS.SCALES.X.DEFAULT_MIN, CHART_CONSTANTS.SCALES.X.DEFAULT_MAX)
    };
  }

  const prices = offers.map(offer => offer.offerPrice);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  const min = Math.max(
    Math.round((minPrice - CHART_CONSTANTS.SCALES.X.PADDING) / 1000) * 1000,
    0
  );
  const max = Math.ceil((maxPrice + CHART_CONSTANTS.SCALES.X.PADDING) / 100) * 100;

  return {
    min,
    max,
    ticks: calculateOptimalTicks(min, max)
  };
}

export function createXScaleConfig(scaleConfig: ScaleConfig) {
  return {
    type: 'linear' as const,
    min: scaleConfig.min,
    max: scaleConfig.max,
    ticks: {
      font: {
        size: 13,
        family: 'Helvetica, Arial, sans-serif',
        weight: '400' as const
      },
      color: 'black',
      callback: (value: string | number) =>
        scaleConfig.ticks.includes(Number(value)) ? value.toString() : ''
    },
    afterBuildTicks: (scale: any) => {
      scale.ticks = scaleConfig.ticks.map(v => ({ value: v }));
    },
    grid: {
      color: (context: any) =>
        scaleConfig.ticks.includes(context.tick.value)
          ? 'rgba(0, 0, 0, 0.08)'
          : 'transparent',
      borderColor: 'rgba(0, 0, 0, 0.08)'
    }
  };
}

export function createYScaleConfig(categoryMapping: CategoryMapping) {
  const categoryCount = Object.keys(categoryMapping).length;

  return {
    type: 'linear' as const,
    min: CHART_CONSTANTS.SCALES.Y.MIN,
    max: categoryCount + 1,
    ticks: {
      stepSize: CHART_CONSTANTS.SCALES.Y.STEP_SIZE,
      callback: () => ''
    },
    grid: {
      color: 'rgba(0, 0, 0, 0.08)',
      borderColor: 'rgba(0, 0, 0, 0.08)'
    }
  };
}

export function createScales(input: ChartCreationInput, categoryMapping: CategoryMapping) {
  const allOffers = [...(input.competitorOffers || []), ...(input.yourOffers || [])];
  const xScaleConfig = calculateXScaleConfig(allOffers);

  return {
    x: createXScaleConfig(xScaleConfig),
    y: createYScaleConfig(categoryMapping)
  };
}

const CATEGORY_IMAGE_MAP: Record<string, string> = {
  [AircraftCategoryNameEnum.LIGHT]: 'light.png',
  [AircraftCategoryNameEnum.SUPER_MID_SIZE]: 'super_mid_size.png',
  [AircraftCategoryNameEnum.TURBOPROP]: 'turboprop.png',
  [AircraftCategoryNameEnum.MID_SIZE]: 'mid_size.png',
  [AircraftCategoryNameEnum.HEAVY]: 'heavy.png',
  [AircraftCategoryNameEnum.ULR]: 'ulr.png'
};

const IMAGE_FOLDER = '../../../../../assets/img/categories';

export function getCategoryImagePath(category: string): string {
  const imageName = CATEGORY_IMAGE_MAP[category];
  return imageName ? `${IMAGE_FOLDER}/${imageName}` : '';
}

export function loadSingleImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

export async function preloadAllCategoryImages(): Promise<Record<string, HTMLImageElement>> {
  const categories = Object.keys(CATEGORY_IMAGE_MAP);

  const imagePromises = categories.map(async (category) => {
    const imagePath = getCategoryImagePath(category);
    const img = await loadSingleImage(imagePath);
    return { category, img };
  });

  const results = await Promise.all(imagePromises);

  return results.reduce<Record<string, HTMLImageElement>>((acc, { category, img }) => {
    if (img) acc[category] = img;
    return acc;
  }, {});
}

export function createTooltipCallbacks(currencyPipe: any) {
  return {
    label: (context: TooltipItem<keyof ChartTypeRegistry>) => {
      const dataPoint = context.raw as ChartPoint;
      const offer = dataPoint?.offer;

      if (!offer) return '';

      const isYourOffer = context.dataset?.label === 'Your Offers';
      const offerPrice = currencyPipe.transform(offer.offerPrice, 'USD');

      return [
        ...(isYourOffer ? ['Your Offer'] : []),
        `Model: ${offer.modelName}`,
        `Offer: ${offerPrice}`,
      ];
    }
  };
}

export function createImageLegendPlugin(
  categoryMapping: CategoryMapping,
  preloadedImages: Record<string, HTMLImageElement>
) {
  return {
    id: 'customImageLegend',
    afterDraw: (chart: any) => {
      const categories = Object.keys(categoryMapping);
      if (categories.length === 0) return;
      const ctx = chart.ctx;
      const yScale = chart.scales['y'];
      const offsetX = chart.chartArea.left - CHART_CONSTANTS.DIMENSIONS.IMAGE.OFFSET_X;

      Object.entries(categoryMapping).forEach(([category, yValue]) => {
        const img = preloadedImages[category];
        if (img && img.complete) {
          const yPixel = yScale.getPixelForValue(yValue);
          ctx.drawImage(
            img,
            offsetX,
            yPixel - CHART_CONSTANTS.DIMENSIONS.IMAGE.HEIGHT / 2,
            CHART_CONSTANTS.DIMENSIONS.IMAGE.WIDTH,
            CHART_CONSTANTS.DIMENSIONS.IMAGE.HEIGHT
          );
        }
      });
    }
  };
}

export function createBaseChartOptions(): ChartConfiguration['options'] {
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: CHART_CONSTANTS.LAYOUT.PADDING_LEFT
      }
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxHeight: 2,
          boxWidth: 2,
          font: {
            size: 13,
            family: 'Helvetica, Arial, sans-serif'
          },
          color: 'black'
        }
      },
      tooltip: {
        backgroundColor: 'rgb(237, 238, 242)',
        titleColor: 'black',
        bodyColor: 'black',
        displayColors: false,
        position: 'average' as const
      }
    }
  };
}

export function createChartOptions(input: ChartCreationInput, categoryMapping: CategoryMapping, currencyPipe: any): ChartConfiguration['options'] {
  const baseOptions = createBaseChartOptions();
  const scales = createScales(input, categoryMapping);
  const tooltipCallbacks = createTooltipCallbacks(currencyPipe);

  return {
    ...baseOptions,
    scales,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        ...baseOptions.plugins?.tooltip,
        callbacks: tooltipCallbacks
      }
    }
  };
}

export function calculateChartHeight(categoryMapping: CategoryMapping): number {
  const categoryCount = Object.keys(categoryMapping).length;
  return categoryCount * CHART_CONSTANTS.DIMENSIONS.HEIGHT_PER_CATEGORY +
    CHART_CONSTANTS.DIMENSIONS.BASE_HEIGHT;
}

export function createCompetitorChartConfiguration<T extends BaseOffer>(
  input: ChartCreationInput<T>,
  currencyPipe: any,
  preloadedImages: Record<string, HTMLImageElement> = {}
): { config: ChartConfiguration; height: number } {
  const allOffers = [...input.competitorOffers, ...input.yourOffers];

  const categoryMapping = createCategoryMapping(allOffers);

  const config: ChartConfiguration = {
    type: 'scatter',
    data: createChartData(input, categoryMapping),
    options: createChartOptions(input, categoryMapping, currencyPipe),
    plugins: [createImageLegendPlugin(categoryMapping, preloadedImages)]
  };

  const height = calculateChartHeight(categoryMapping);

  return { config, height };
}
